"use client";

import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import Image from "next/legacy/image";

export default function BlogsSection() {
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const canvasRef = useRef(null);
  const touchTimeoutRef = useRef(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle touch events for mobile
  const handleTouchStart = (index) => {
    if (isMobile) {
      // Clear any existing timeout to prevent premature reset
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
      setHoveredIndex(index);
    }
  };

  const handleTouchEnd = () => {
    if (isMobile) {
      // Delay reset to keep the expanded state briefly after tap
      touchTimeoutRef.current = setTimeout(() => {
        setHoveredIndex(null);
      }, 500); // 500ms delay for better UX
    }
  };

  // Particle Animation Logic
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    function resizeCanvas() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const numParticles = isMobile ? 50 : 100; // Reduce particles for mobile
    const particles = [];

    class Particle {
      constructor(x, y, radius, speed) {
        this.x = x;
        this.y = y;
        this.radius = radius;
        this.speed = speed;
        this.angle = Math.random() * Math.PI * 2;
      }

      update() {
        this.x += Math.cos(this.angle) * this.speed;
        this.y += Math.sin(this.angle) * this.speed;

        // Bounce off edges
        if (this.x < 0 || this.x > canvas.width) this.angle = Math.PI - this.angle;
        if (this.y < 0 || this.y > canvas.height) this.angle = -this.angle;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
        ctx.fill();
      }
    }

    // Initialize particles
    for (let i = 0; i < numParticles; i++) {
      particles.push(
        new Particle(
          Math.random() * canvas.width,
          Math.random() * canvas.height,
          Math.random() * 4 + 1,
          Math.random() * 0.5 + 0.2
        )
      );
    }

    // Connect particles
    function connectParticles() {
      for (let a = 0; a < particles.length; a++) {
        for (let b = a + 1; b < particles.length; b++) {
          const dist = Math.hypot(
            particles[a].x - particles[b].x,
            particles[a].y - particles[b].y
          );

          if (dist < 120) {
            ctx.strokeStyle = `rgba(255, 255, 255, ${1 - dist / 120})`;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.stroke();
          }
        }
      }
    }

    // Animation Loop
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });
      connectParticles();
      requestAnimationFrame(animate);
    }

    animate();

    // Cleanup
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, [isMobile]);

  return (
    <motion.section
      className="bg-gradient-to-r from-[#00063A] via-[#425fae] to-[#00063A] pt-12 pb-16 px-8 relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={sectionVariants}
    >
      <h1 className="text-center text-white py-0 mt-4 font-bold">Blogs</h1>
      {/* Canvas for Particle Animation */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0 }}
      />

      <div
        className={`mt-14 flex space-x-4 mx-auto overflow-x-auto ${
          isMobile ? "px-4" : "mr-16 ml-16"
        }`}
        style={{ minWidth: isMobile ? "auto" : "40px" }}
      >
        {Array(5)
          .fill()
          .map((_, index) => (
            <div
              key={index}
              className={`transition-all duration-500 ease-in-out cursor-pointer overflow-hidden relative
                ${
                  isMobile
                    ? hoveredIndex === index
                      ? "flex-[3]"
                      : "flex-[1]"
                    : "flex-[1.67] group-hover:flex-[1] hover:!flex-[3]"
                }`}
              style={{
                width: isMobile ? "100px" : "117.15789794921875px",
                height: isMobile
                  ? index % 2 === 0
                    ? "300px"
                    : "270px"
                  : index % 2 === 0
                  ? "442.5094909667969px"
                  : "399.2757873535156px",
              }}
              onMouseEnter={() => !isMobile && setHoveredIndex(index)}
              onMouseLeave={() => !isMobile && setHoveredIndex(null)}
              onTouchStart={(e) => {
                e.preventDefault(); // Prevent scrolling during tap
                handleTouchStart(index);
              }}
              onTouchEnd={handleTouchEnd}
            >
              <Image
                src={`/Hero-${index + 1}.jpg`}
                alt={`Hero-${index + 1}`}
                layout="fill"
                className="object-cover"
              />
            </div>
          ))}
      </div>
    </motion.section>
  );
}